import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { VISIBILITY_PACKAGES } from './constants';

interface PackageCardProps {
    pkg: typeof VISIBILITY_PACKAGES[0];
    isUpgrade?: boolean;
    purchaseVisibility: (lookupKey: string, days: number, weight: number) => void;
    buying: string | null;
}

const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR'
    }).format(amount);
};

export const PackageCard: React.FC<PackageCardProps> = ({
    pkg,
    isUpgrade = false,
    purchaseVisibility,
    buying
}) => {
    const { t } = useTranslation();

    // Upgrade Variant (Compact)
    if (isUpgrade) {
        return (
            <button
                onClick={() => purchaseVisibility(pkg.id, pkg.days, pkg.weight)}
                disabled={!!buying}
                className={`p-4 rounded-xl border transition-all text-left group relative border-gray-200 hover:border-clip-500`}
            >
                <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-900">{t(pkg.title)}</span>
                    {pkg.badge && (
                        <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                            {t(pkg.badge)}
                        </span>
                    )}
                </div>
                <p className="text-gray-500 text-sm mb-3">{t('billing.upgrade_days', { days: pkg.days })}</p>
                <div className="flex items-end justify-between">
                    {pkg.originalPrice ? (
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-400 line-through">{formatAmount(pkg.originalPrice)}</span>
                            <span className="text-xl font-bold text-gray-900">{formatAmount(pkg.price)}</span>
                        </div>
                    ) : (
                        <span className="text-xl font-bold text-gray-900">{formatAmount(pkg.price)}</span>
                    )}
                    {buying === pkg.id ? <Loader2 className="animate-spin text-clip-500" /> : <ArrowUpRight className="text-gray-300 group-hover:text-clip-500" />}
                </div>
            </button>
        );
    }

    // Standard Variant (Detailed)
    return (
        <div className={`p-6 rounded-2xl border transition-all flex flex-col sm:flex-row items-center gap-6 ${pkg.containerClasses} relative`}>
            {pkg.topBadge && (
                <div className="absolute -top-3 right-5 bg-clip-500 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                    {t(pkg.topBadge)}
                </div>
            )}
            <div className={`${pkg.iconBg} p-4 rounded-xl ${pkg.iconColor}`}>
                <pkg.icon size={32} />
            </div>
            <div className="flex-1 text-center sm:text-left">
                <h3 className="font-bold text-lg text-gray-900">{t(pkg.title)}</h3>

                <p className="text-gray-500 text-sm">{t(pkg.description)}</p>
            </div>
            <div className="text-center sm:text-right min-w-[120px]">
                <div className="flex items-end justify-end gap-x-2 mb-3">
                    {pkg.originalPrice && <span className="text-xs text-gray-400 line-through">{formatAmount(pkg.originalPrice)}</span>}
                    {pkg.originalPrice ? (
                        <div className="flex flex-col items-center sm:items-end">
                            {pkg.badge && (
                                <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full mb-2">
                                    {t(pkg.badge)}
                                </span>
                            )}
                            <p className="text-2xl font-bold text-gray-900">{formatAmount(pkg.price)}</p>
                        </div>
                    ) : (
                        <p className="text-2xl font-bold text-gray-900">{formatAmount(pkg.price)}</p>
                    )}
                </div>
                <button
                    onClick={() => purchaseVisibility(pkg.id, pkg.days, pkg.weight)}
                    disabled={!!buying}
                    className={`w-full py-2 font-bold rounded-lg text-sm transition-colors flex items-center justify-center gap-2 ${pkg.buttonClasses}`}
                >
                    {buying === pkg.id && <Loader2 size={14} className="animate-spin" />}
                    {t('billing.buy_btn')}
                </button>
            </div>
        </div>
    );
};
